import React from 'react';

import { Box, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';

interface Props<T extends string | number> {
  title: string;
  items: Array<T>;
  value: 'all' | Array<T>;
  enabledItems: Set<T>;
  formatItem: (item: T) => string;
  onChange: (value: 'all' | Array<T>) => void;
}

export default function ApplyToColumn<T extends string | number>({
  title,
  items,
  value,
  enabledItems,
  formatItem,
  onChange,
}: Props<T>) {
  const isAll = value === 'all';
  const selected = isAll ? [] : value;

  const toggleSelectAll = (checked: boolean) => onChange(checked ? 'all' : []);

  const toggleItem = (item: T, checked: boolean) => {
    const current = isAll ? items.filter((entry) => enabledItems.has(entry)) : selected;
    const next = checked ? Array.from(new Set([...current, item])) : current.filter((entry) => entry !== item);
    onChange(next.length === items.length ? 'all' : next);
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 1 }}>
        {title}
      </Typography>
      <Divider />
      <Stack sx={{ p: 1 }}>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={isAll} onChange={(event) => toggleSelectAll(event.target.checked)} />
          }
          label={<Typography fontWeight={600}>Select all</Typography>}
        />
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            None entered yet
          </Typography>
        ) : (
          <>
            <Divider sx={{ my: 0.5 }} />
            {items.map((item) => (
              <FormControlLabel
                key={item}
                control={
                  <Checkbox
                    size="small"
                    checked={enabledItems.has(item) && (isAll || selected.includes(item))}
                    disabled={!enabledItems.has(item)}
                    onChange={(event) => toggleItem(item, event.target.checked)}
                  />
                }
                label={formatItem(item)}
              />
            ))}
          </>
        )}
      </Stack>
    </Box>
  );
}
